import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Perfil, CreatePerfilRequest, UpdatePerfilRequest } from '../models/perfil.interface';
import { environment } from '../../../../environments/environment';
import { ApiEnvelope } from '../../../shared/models/api-envelope';
import { PerfilMapper } from '../models/perfil.mapper';
import { PerfilDto } from '../models/perfil.dto';

@Injectable({
  providedIn: 'root'
})
export class PerfilService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.baseUrl.generic}/Perfil`;

  private unwrapResponse<T>() {
    return map((response: T | ApiEnvelope<T>) => {
      const maybe = response as ApiEnvelope<T>;
      return maybe && (maybe as { data?: T }).data !== undefined
        ? (maybe.data as T)
        : (response as T);
    });
  }

  private normalizePerfil(raw: PerfilDto): Perfil {
    return PerfilMapper.fromDto(raw);
  }

  private normalizeList(list: PerfilDto[] | Perfil[]): Perfil[] {
    return PerfilMapper.fromDtoList(list as PerfilDto[]);
  }

  getPerfiles(includeRangos: boolean = false): Observable<Perfil[]> {
    const params = new HttpParams().set('includeRangos', includeRangos.toString());
    return this.http.get<ApiEnvelope<PerfilDto[]>>(`${this.baseUrl}/GetAll`, { params }).pipe(
      this.unwrapResponse<PerfilDto[]>(),
      map((res: PerfilDto[]) => this.normalizeList(res))
    );
  }

  getPerfilById(id: number, includeRangos: boolean = false): Observable<Perfil> {
    const params = new HttpParams()
      .set('id', id.toString())
      .set('includeRangos', includeRangos.toString());
    return this.http.get<ApiEnvelope<PerfilDto>>(`${this.baseUrl}/GetByID`, { params }).pipe(
      this.unwrapResponse<PerfilDto>(),
      map((res: PerfilDto) => this.normalizePerfil(res))
    );
  }

  createPerfil(perfil: CreatePerfilRequest): Observable<Perfil> {
    return this.http.post<ApiEnvelope<PerfilDto>>(`${this.baseUrl}/Create`, perfil).pipe(
      this.unwrapResponse<PerfilDto>(),
      map((res: PerfilDto) => this.normalizePerfil(res))
    );
  }

  updatePerfil(id: number, perfil: UpdatePerfilRequest): Observable<boolean> {
    return this.http
      .put<ApiEnvelope<boolean>>(`${this.baseUrl}/${id}`, perfil)
      .pipe(this.unwrapResponse<boolean>());
  }

  deletePerfil(id: number): Observable<boolean> {
    return this.http
      .delete<ApiEnvelope<boolean>>(`${this.baseUrl}/${id}`)
      .pipe(this.unwrapResponse<boolean>());
  }
}
